import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { getLocalization } from './AuthLocalization';
import { secureAuthStorage } from '@/utils/secureCookies';
import { logger } from '@/utils/logger';
import { AuthError } from '@supabase/supabase-js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tabSync } from '@/utils/tabSync';
import { AuthView } from './types';
import { EmailInput } from './form/EmailInput';
import { PasswordInput } from './form/PasswordInput';
import { AuthFormHeader } from './form/AuthFormHeader';
import { AuthFormLinks } from './form/AuthFormLinks';
import { SupportMessage } from './form/SupportMessage';
import { GoogleSignInButton } from './form/GoogleSignInButton';
import { Separator } from '@/components/ui/separator';
import { VerificationCodeInput } from './form/VerificationCodeInput';
import { ConfirmResetPasswordForm } from './form/ConfirmResetPasswordForm';
import { PremiumTrialMessage } from './form/PremiumTrialMessage';
import { motion } from 'framer-motion';
// Import our standardized error types
import { AppError, AuthenticationError, toAppError } from '@/types/errors';

interface AuthFormProps {
	view?: AuthView;
}

export const AuthForm = ({ view = 'sign_in' }: AuthFormProps) => {
	const { toast } = useToast();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isPasswordRecoveryValid, setIsPasswordRecoveryValid] = useState(false);
	const [verificationError, setVerificationError] = useState<string | undefined>();
	const [currentView, setCurrentView] = useState<AuthView>(view);

	useEffect(() => {
		setCurrentView(view);
	}, [view]);

	const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		
		// Input validation
		if (!email.trim()) {
			toast({
				variant: 'destructive',
				title: 'Validation Error',
				description: 'Email is required'
			});
			return;
		}
		
		if (!password) {
			toast({
				variant: 'destructive',
				title: 'Validation Error',
				description: 'Password is required'
			});
			return;
		}
		
		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			toast({
				variant: 'destructive',
				title: 'Validation Error',
				description: 'Please enter a valid email address'
			});
			return;
		}
		
		setIsLoading(true);

		try {
			logger.debug('Attempting to sign in user');
			const { error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			if (error) throw error;

			logger.info('User signed in successfully');
			toast({
				title: 'Signed in successfully!',
				description: 'Welcome back!',
				duration: 1500, // Shorter duration (1.5 seconds)
				className: 'auth-toast', // Add class for styling
			});

			navigate('/', { replace: true });
		} catch (error: unknown) {
			// Convert to our standard error type
			const appError = toAppError(error);
			logger.error('Sign in failed', appError);
			toast({
				variant: 'destructive',
				title: 'Error signing in',
				description: appError.message,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			// Validate inputs before sending to Supabase
			if (!email.trim()) {
				toast({
					variant: 'destructive',
					title: 'Validation Error',
					description: 'Email is required'
				});
				return;
			}
			
			if (!password) {
				toast({
					variant: 'destructive',
					title: 'Validation Error',
					description: 'Password is required'
				});
				return;
			}
			
			// Validate email format
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email)) {
				toast({
					variant: 'destructive',
					title: 'Validation Error',
					description: 'Please enter a valid email address'
				});
				return;
			}
			
			// Directly attempt signup and let Supabase handle existing email checks
			const { data, error } = await supabase.auth.signUp({
				email,
				password,
				options: {
					emailRedirectTo: window.location.origin + '/auth?view=sign_in',
				},
			});

			// Handle signup errors
			if (error) {
				// Check specific error messages from Supabase
				if (error.message && error.message.includes('already registered')) {
					logger.info('User tried to sign up with an existing email');
					toast({
						variant: 'destructive',
						title: 'Email already registered',
						description: 'This email is already registered. Please sign in or reset your password.',
					});
					setCurrentView('sign_in');
					return;
				}
				throw error;
			}

			// Check if user is null which indicates the email already exists
			if (data?.user === null) {
				logger.info('User tried to sign up with an existing email');
				toast({
					variant: 'destructive',
					title: 'Email already registered',
					description: 'This email is already registered. Please sign in or reset your password.',
				});
				setCurrentView('sign_in');
				return;
			}

			// Store email securely in cookies instead of localStorage
			secureAuthStorage.storeVerificationEmail(email);
			logger.debug('Verification email stored securely for signup');

			toast({
				title: 'Check your email',
				description: "We've sent you a verification code to confirm your email address.",
			});

			// Move to verification code view
			setCurrentView('verification_code');
		} catch (error: unknown) {
			// Convert to our standard error type
			const appError = toAppError(error);
			toast({
				variant: 'destructive',
				title: 'Error',
				description: appError.message,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyCode = async (code: string) => {
		try {
			setVerificationError(undefined);
			const { error } = await supabase.auth.verifyOtp({
				email,
				token: code,
				type: 'signup',
			});

			if (error) {
				logger.error('Verification error', error);
				setVerificationError(error.message);
				throw error;
			}

			// Clean up stored email from secure storage
			secureAuthStorage.clearVerificationEmail();
			logger.debug('Verification email cleared after successful verification');

			toast({
				title: 'Email verified successfully!',
				description: "Your account has been created and you're now signed in.",
			});
		} catch (error: unknown) {
			// Error is already handled with setVerificationError
			logger.error('Verification error', error as AppError | AuthError);
		}
	};

	const handleResendCode = async () => {
		try {
			setVerificationError(undefined);
			const { error } = await supabase.auth.resend({
				type: 'signup',
				email,
			});

			if (error) throw error;

			toast({
				title: 'Code resent',
				description: 'A new verification code has been sent to your email.',
			});
		} catch (error: unknown) {
			// Convert to our standard error type
			const appError = toAppError(error);
			toast({
				variant: 'destructive',
				title: 'Error resending code',
				description: appError.message,
			});
		}
	};

	const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			logger.debug('Initiating password reset for email', { email });

			const { error } = await supabase.auth.resetPasswordForEmail(email, {
				redirectTo: `${window.location.origin}/reset-password`,
			});

			if (error) throw error;

			toast({
				title: 'Password reset email sent',
				description:
					'Check your email for a link to reset your password. If it doesn\'t appear within a few minutes, check your spam folder.',
			});

			// Move to sign in view
			setCurrentView('sign_in');
		} catch (error: unknown) {
			// Convert to our standard error type
			const appError = toAppError(error);
			logger.error('Error sending reset email', appError);
			toast({
				variant: 'destructive',
				title: 'Error sending reset email',
				description: appError.message,
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			logger.debug('Attempting to reset password');
			
			const { data: { session } } = await supabase.auth.getSession();
			if (!session) {
				throw new Error('No active session found');
			}

			// Validate passwords
			const hasErrors = password.length < 8 || password !== confirmPassword;
			if (hasErrors) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Please make sure your passwords match and are at least 8 characters long.',
				});
				setIsLoading(false);
				return;
			}

			// Handle password reset with token
			const { error } = await supabase.auth.updateUser({
				password,
			});

			if (error) throw error;

			logger.info('Password updated successfully');
			toast({
				title: 'Password updated successfully',
				description: 'Your password has been updated. You can now sign in with your new password.',
			});

			// Redirect to sign in page
			navigate('/auth?view=sign_in', { replace: true });
		} catch (error: unknown) {
			// Convert to our standard error type
			const appError = toAppError(error);
			logger.error('Password reset error', appError);

			// Check if error is due to expired or invalid token
			const isExpiredOrInvalid = appError.message?.includes('invalid');
			
			toast({
				variant: 'destructive',
				title: isExpiredOrInvalid ? 'Invalid or expired reset link' : 'Error',
				description: isExpiredOrInvalid
					? 'Your password reset link has expired or is invalid. Please request a new one.'
					: appError.message,
			});

			if (isExpiredOrInvalid) {
				// Clean up stored token on error
				sessionStorage.removeItem('passwordResetToken');
				navigate('/auth?view=forgot_password', { replace: true });
			}
		} finally {
			setIsLoading(false);
		}
	};

	const getFormHandler = (viewType: AuthView) => {
		switch (viewType) {
			case 'sign_in':
				return handleSignIn;
			case 'sign_up':
				return handleSignUp;
			case 'reset_password':
				return handleResetPassword;
			case 'forgot_password':
				return handleForgotPassword;
			default:
				return handleSignIn;
		}
	};

	// Restore email from secure storage if available for verification code view
	useEffect(() => {
		if (currentView === 'verification_code' && !email) {
			const storedEmail = secureAuthStorage.getVerificationEmail();
			if (storedEmail) {
				logger.debug('Retrieved verification email from secure storage');
				setEmail(storedEmail);
			}
		}
	}, [currentView, email]);

	useEffect(() => {
		if (currentView === 'sign_in') {
			const checkAuth = async () => {
				const { data: { session }, error } = await supabase.auth.getSession();
				if (session) {
					navigate('/', { replace: true });
				}
			};
			checkAuth();
		}
	}, [currentView, navigate]);

	return (
		<div className="px-4 py-3 sm:py-6 sm:px-8">
			<motion.div 
				initial={{ opacity: 0, scale: 0.9 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.3 }}
				className="flex justify-center mb-2"
			>
				<div className="relative">
					<div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur opacity-30"></div>
					<img src="/strykr-logo.jpeg" alt="Strykr.ai" className="relative h-14 w-14 rounded-full ring-2 ring-blue-500/30" />
				</div>
			</motion.div>
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.1 }}
			>
				<AuthFormHeader view={currentView} />
			</motion.div>
			{(currentView === 'sign_in' || currentView === 'sign_up') && (
				<motion.div 
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.3, delay: 0.2 }}
				>
					<GoogleSignInButton />
					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<span className="w-full border-t border-gray-700" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-background px-2 text-muted-foreground">
								Or continue with email
							</span>
						</div>
					</div>
				</motion.div>
			)}

			{currentView === 'verification_code' ? (
				<VerificationCodeInput
					email={email}
					onSubmit={handleVerifyCode}
					onResend={handleResendCode}
					error={verificationError}
				/>
			) : currentView === 'confirm_reset_password' ? (
				<ConfirmResetPasswordForm email={email} />
			) : (
				<form onSubmit={getFormHandler(currentView)} className="space-y-4 sm:space-y-6">
					<div className="space-y-3 sm:space-y-4">
						{currentView === 'reset_password' ? (<></>) : (
						<EmailInput
							email={email}
							setEmail={setEmail}
							label={getLocalization(currentView).email_label}
							placeholder={getLocalization(currentView).email_input_placeholder}
						/>
						)}

						{(currentView === 'sign_in' ||
							currentView === 'sign_up' ||
							currentView === 'reset_password') && (
							<PasswordInput
								password={password}
								setPassword={setPassword}
								label={getLocalization(currentView).password_label}
								placeholder={getLocalization(currentView).password_input_placeholder}
								id="new-password-input"
								showValidation={currentView === 'sign_up'}
							/>
						)}

						{currentView === 'reset_password' && (
							<PasswordInput
								password={confirmPassword}
								setPassword={setConfirmPassword}
								label="Confirm Password"
								placeholder="Confirm your new password"
								id="confirm-password-input"
							/>
						)}

						<SupportMessage
							show={currentView === 'forgot_password' || currentView === 'reset_password'}
						/>
					</div>

					<div className="space-y-2 sm:space-y-4 mt-2 sm:mt-4">

						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, delay: 0.4 }}
						>
							<Button
								type="submit"
								disabled={isLoading}
								className={`w-full transition-all duration-200 font-medium text-white py-2 sm:py-5 ${
									currentView === 'sign_up' 
										? 'bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 shadow-lg shadow-emerald-700/20' 
										: 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shadow-lg shadow-blue-700/20'
								}`}
							>
								{isLoading ? (
									<div className="flex items-center justify-center gap-2">
										<svg className="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										{getLocalization(currentView).loading_button_label}
									</div>
								) : (
									<span className="text-sm sm:text-base font-semibold">{getLocalization(currentView).button_label}</span>
								)}
							</Button>
						</motion.div>

						{currentView === 'sign_up' && (
							<motion.div 
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, delay: 0.5 }}
							>
								<PremiumTrialMessage />
							</motion.div>
						)}
						<AuthFormLinks view={currentView} />
						

					</div>
				</form>
			)}
		</div>
	);
};
